/**
 * HandoverModal — bulk handover wizard for when a therapist goes on leave.
 *
 * Props:
 *   leave           — the Leave appointment row (has _id, username, start, end, leaveType)
 *   allStaff        — array of { username, roles, ... }
 *   existingHandover — existing Handover doc (or null)
 *   onClose()       — called when modal closes (should refetch handovers)
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { canDo } from '../utils/canDo';
import {
  FaTimes, FaExchangeAlt, FaExclamationTriangle,
  FaCheck, FaUndo, FaCalendarPlus, FaSave, FaPlus, FaUserPlus
} from 'react-icons/fa';

const BASE = process.env.REACT_APP_URL;

const HandoverModal = function ({ leave, allStaff, existingHandover, onClose }) {
  var auth = useAuth().auth;
  const canRevert = canDo(auth, 'leaves.revert');

  // Handover doc state
  var [handover, setHandover] = useState(existingHandover || null);
  var [loading, setLoading] = useState(false);
  var [patients, setPatients] = useState([]);     // grouped patient list from appointments
  var [items, setItems] = useState([]);            // handover items (1 per patient)
  var [notes, setNotes] = useState('');
  var [validations, setValidations] = useState({}); // { therapist__date: { onLeave, appointmentCount } }
  var [submitResult, setSubmitResult] = useState(null);
  var [extending, setExtending] = useState(false);
  var [extendDate, setExtendDate] = useState('');
  var [adhocSearch, setAdhocSearch] = useState('');
  var [adhocResults, setAdhocResults] = useState([]);
  var [showAdhoc, setShowAdhoc] = useState(false);

  const therapist = leave.username;
  const startDate = new Date(leave.start);
  const endDate = new Date(leave.end);

  // Format date for display
  function fmtDate(d) {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // ── Fetch patients with appointments in the leave period ──────────────
  const fetchPatients = useCallback(function () {
    const s = startDate.toISOString();
    const e = endDate.toISOString();
    api.get(BASE + '/appointments?username=' + therapist + '&start=' + s + '&end=' + e)
      .then(function (res) {
        const appts = (res.data || []).filter(a => a.type !== 'Leave' && a.status !== 'Completed');
        // Group by patientCard
        const map = {};
        appts.forEach(function (a) {
          if (!a.patientCard) return;
          if (!map[a.patientCard]) {
            map[a.patientCard] = { patientCard: a.patientCard, patientName: a.name || a.patientCard, appointments: [] };
          }
          map[a.patientCard].appointments.push(a);
        });
        var list = Object.values(map).sort(function (a, b) { return a.patientName.localeCompare(b.patientName); });
        setPatients(list);

        // Initialize items from existing handover or fresh
        if (handover && handover.items && handover.items.length > 0) {
          // Map existing items, augment with fetched appointment info
          var mapped = handover.items.map(function (it) {
            var pat = list.find(p => p.patientCard === it.patientCard);
            return {
              ...it,
              patientName: pat ? pat.patientName : it.patientName || it.patientCard,
              appointments: pat ? pat.appointments : [],
            };
          });
          // Add any new patients not in handover items
          list.forEach(function (pat) {
            if (!mapped.find(m => m.patientCard === pat.patientCard)) {
              mapped.push({
                patientCard: pat.patientCard,
                patientName: pat.patientName,
                substituteTherapist: '',
                ktNotes: '',
                status: 'pending',
                appointments: pat.appointments,
              });
            }
          });
          setItems(mapped);
        } else {
          setItems(list.map(function (pat) {
            return {
              patientCard: pat.patientCard,
              patientName: pat.patientName,
              substituteTherapist: '',
              ktNotes: '',
              status: 'pending',
              appointments: pat.appointments,
            };
          }));
        }
      })
      .catch(function (err) { console.error('[HandoverModal] fetch patients error:', err); });
  }, [therapist, leave.start, leave.end]);

  useEffect(function () {
    fetchPatients();
    if (existingHandover) {
      setNotes(existingHandover.notes || '');
    }
  }, []);

  // ── Validate substitute ──────────────────────────────────────────────
  function validateSubstitute(sub, dateStr) {
    var key = sub + '__' + dateStr;
    if (validations[key]) return; // already checked
    api.get(BASE + '/handovers/validate-substitute?therapist=' + sub + '&date=' + dateStr)
      .then(function (res) {
        setValidations(function (prev) { return { ...prev, [key]: res.data }; });
      })
      .catch(function () { /* ignore */ });
  }

  function onSubstituteChange(idx, sub) {
    var updated = items.slice();
    updated[idx] = { ...updated[idx], substituteTherapist: sub };
    setItems(updated);
    // Validate for each day in range
    if (sub) {
      var d = new Date(startDate);
      while (d <= endDate) {
        validateSubstitute(sub, d.toISOString().split('T')[0]);
        d.setDate(d.getDate() + 1);
      }
    }
  }

  function onKtNotesChange(idx, val) {
    var updated = items.slice();
    updated[idx] = { ...updated[idx], ktNotes: val };
    setItems(updated);
  }

  function onSkipToggle(idx) {
    var updated = items.slice();
    updated[idx] = { ...updated[idx], status: updated[idx].status === 'skipped' ? 'pending' : 'skipped' };
    setItems(updated);
  }

  function getSubWarning(sub) {
    if (!sub) return null;
    var warnings = [];
    var d = new Date(startDate);
    while (d <= endDate) {
      var key = sub + '__' + d.toISOString().split('T')[0];
      var v = validations[key];
      if (v) {
        if (v.onLeave) warnings.push({ type: 'error', msg: sub + ' is on leave on ' + fmtDate(d) });
        else if (v.appointmentCount >= 8) warnings.push({ type: 'warn', msg: sub + ' has ' + v.appointmentCount + ' sessions on ' + fmtDate(d) });
      }
      d.setDate(d.getDate() + 1);
    }
    return warnings.length > 0 ? warnings : null;
  }

  // ── Ad-hoc patient search (patients assigned to this therapist but no appointment in leave period)
  function searchAdhocPatients(q) {
    setAdhocSearch(q);
    if (q.length < 2) { setAdhocResults([]); return; }
    api.get(BASE + '/appointments?username=' + therapist + '&search=' + encodeURIComponent(q))
      .then(function (res) {
        // Get unique patients not already in items
        var existing = new Set(items.map(function (it) { return it.patientCard; }));
        var map = {};
        (res.data || []).forEach(function (a) {
          if (!a.patientCard || existing.has(a.patientCard) || a.type === 'Leave') return;
          if (!map[a.patientCard]) map[a.patientCard] = { patientCard: a.patientCard, patientName: a.name || a.patientCard };
        });
        setAdhocResults(Object.values(map).slice(0, 10));
      })
      .catch(function () { setAdhocResults([]); });
  }

  function addAdhocPatient(pat) {
    setItems(function (prev) {
      if (prev.find(function (it) { return it.patientCard === pat.patientCard; })) return prev;
      return [...prev, {
        patientCard: pat.patientCard,
        patientName: pat.patientName,
        substituteTherapist: '',
        ktNotes: '',
        status: 'pending',
        appointments: [],
        isAdhoc: true,
      }];
    });
    setAdhocSearch('');
    setAdhocResults([]);
  }

  // ── Staff list for dropdown (exclude the therapist on leave) ──────────
  var staffOptions = (allStaff || [])
    .filter(function (u) { return u.username !== therapist && u.status !== 'inactive' && u.status !== 'terminated'; })
    .sort(function (a, b) { return a.username.localeCompare(b.username); });

  // ── Save / Create / Submit ───────────────────────────────────────────
  async function handleSaveDraft() {
    setLoading(true);
    try {
      var itemsToSave = items.map(function (it) {
        return {
          patientCard: it.patientCard,
          patientName: it.patientName,
          substituteTherapist: it.substituteTherapist,
          ktNotes: it.ktNotes,
          status: it.status,
        };
      });

      if (handover && handover._id) {
        // Update existing draft
        var res = await api.put(BASE + '/handovers/' + handover._id, { items: itemsToSave, notes: notes });
        setHandover(res.data);
        alert('Draft saved!');
      } else {
        // Create new handover
        var createRes = await api.post(BASE + '/handovers', {
          therapistOnLeave: therapist,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          leaveType: leave.leaveType || 'planned',
          leaveAppointmentIds: [leave._id],
          notes: notes,
        });
        var newDoc = createRes.data;
        // Then update with items
        var updateRes = await api.put(BASE + '/handovers/' + newDoc._id, { items: itemsToSave, notes: notes });
        setHandover(updateRes.data);
        alert('Handover draft created!');
      }
    } catch (err) {
      alert('Error saving: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  }

  async function handleSubmit() {
    // Validate all non-skipped items have a substitute
    var missing = items.filter(function (it) { return it.status !== 'skipped' && !it.substituteTherapist; });
    if (missing.length > 0) {
      alert('Please assign a substitute for all patients (or skip them): ' + missing.map(m => m.patientName).join(', '));
      return;
    }

    if (!window.confirm('This will reassign all appointments and checklists. Proceed?')) return;

    setLoading(true);
    try {
      // Save first if needed
      if (!handover || !handover._id) {
        await handleSaveDraft();
      } else {
        // Update items before submit
        var itemsToSave = items.map(function (it) {
          return { patientCard: it.patientCard, patientName: it.patientName, substituteTherapist: it.substituteTherapist, ktNotes: it.ktNotes, status: it.status };
        });
        await api.put(BASE + '/handovers/' + handover._id, { items: itemsToSave, notes: notes });
      }

      // Submit
      var hid = handover?._id;
      if (!hid) { alert('Please save draft first.'); setLoading(false); return; }
      var res = await api.post(BASE + '/handovers/' + hid + '/submit');
      setSubmitResult(res.data);
      setHandover(res.data.handover);
      alert('Handover submitted! ' + res.data.appointmentsReassigned + ' appointments reassigned, ' + res.data.checklistsTransferred + ' checklists transferred.');
    } catch (err) {
      alert('Submit error: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  }

  async function handleRevert() {
    if (!handover || !handover._id) return;
    if (!window.confirm('Revert all handover reassignments? Appointments will return to ' + therapist + '.')) return;
    setLoading(true);
    try {
      var res = await api.post(BASE + '/handovers/' + handover._id + '/revert');
      setHandover(res.data.handover);
      alert('Handover reverted! ' + res.data.appointmentsReverted + ' appointments restored.');
    } catch (err) {
      alert('Revert error: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  }

  async function handleExtend() {
    if (!extendDate || !handover?._id) return;
    setLoading(true);
    try {
      var res = await api.post(BASE + '/handovers/' + handover._id + '/extend', { newEndDate: extendDate });
      setHandover(res.data.handover);
      setExtending(false);
      alert('Leave extended to ' + fmtDate(extendDate));
    } catch (err) {
      alert('Extend error: ' + (err.response?.data?.message || err.message));
    }
    setLoading(false);
  }

  var isActive = handover && handover.status === 'active';
  var isReverted = handover && handover.status === 'reverted';
  var isDraft = !handover || handover.status === 'draft';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 800,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaExchangeAlt style={{ color: '#4338ca' }} /> Handover Plan
            </h3>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              <strong>{therapist}</strong> &middot; {fmtDate(startDate)} — {fmtDate(endDate)}
              {leave.leaveType && (
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                  background: leave.leaveType === 'unplanned' ? '#fef3c7' : '#e0e7ff',
                  color: leave.leaveType === 'unplanned' ? '#92400e' : '#3730a3',
                }}>
                  {leave.leaveType === 'unplanned' ? 'Unplanned' : 'Planned'}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {isActive && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: '#dcfce7', color: '#16a34a' }}>
                Active
              </span>
            )}
            {isReverted && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: '#f1f5f9', color: '#64748b' }}>
                Reverted
              </span>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>

          {/* Overall notes */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Overall Handover Notes</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              disabled={!isDraft}
              placeholder="General notes about this handover..."
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: isDraft ? '#f8fafc' : '#f1f5f9', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          {/* Patient list */}
          {patients.length === 0 && items.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 14 }}>
              No patient appointments found for this leave period.
            </div>
          )}

          {items.map(function (item, idx) {
            var warnings = getSubWarning(item.substituteTherapist);
            var isSkipped = item.status === 'skipped';

            return (
              <div key={item.patientCard} style={{
                background: isSkipped ? '#f8fafc' : '#fff', borderRadius: 12, padding: '14px 16px',
                marginBottom: 10, border: '1px solid ' + (isSkipped ? '#e2e8f0' : '#e0e7ff'),
                opacity: isSkipped ? 0.6 : 1, transition: 'opacity 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  {/* Patient info */}
                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{item.patientName}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {item.patientCard} &middot; {(item.appointments || []).length} session(s)
                      {item.isAdhoc && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8' }}>Ad-hoc</span>}
                    </div>
                  </div>

                  {/* Substitute dropdown */}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <select
                      value={item.substituteTherapist}
                      onChange={e => onSubstituteChange(idx, e.target.value)}
                      disabled={!isDraft || isSkipped}
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: 8,
                        border: '1px solid ' + (warnings && warnings.some(w => w.type === 'error') ? '#fca5a5' : '#cbd5e1'),
                        fontSize: 13, background: isDraft && !isSkipped ? '#fff' : '#f1f5f9', fontWeight: 600,
                      }}
                    >
                      <option value="">Select substitute...</option>
                      {staffOptions.map(function (u) {
                        return <option key={u.username} value={u.username}>{u.username}</option>;
                      })}
                    </select>
                    {warnings && warnings.map(function (w, wi) {
                      return (
                        <div key={wi} style={{
                          fontSize: 11, marginTop: 3, fontWeight: 600,
                          color: w.type === 'error' ? '#dc2626' : '#d97706',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <FaExclamationTriangle size={10} /> {w.msg}
                        </div>
                      );
                    })}
                  </div>

                  {/* Skip checkbox */}
                  {isDraft && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b', cursor: 'pointer', flexShrink: 0 }}>
                      <input type="checkbox" checked={isSkipped} onChange={() => onSkipToggle(idx)} />
                      Skip
                    </label>
                  )}
                </div>

                {/* KT Notes */}
                {!isSkipped && (
                  <div style={{ marginTop: 8 }}>
                    <textarea rows={1} value={item.ktNotes} onChange={e => onKtNotesChange(idx, e.target.value)}
                      disabled={!isDraft}
                      placeholder="KT notes for substitute..."
                      style={{
                        width: '100%', padding: '6px 10px', borderRadius: 6,
                        border: '1px solid #e2e8f0', fontSize: 12, background: isDraft ? '#fafbfc' : '#f1f5f9',
                        resize: 'vertical', boxSizing: 'border-box',
                      }} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Add ad-hoc patient */}
          {isDraft && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
              {!showAdhoc ? (
                <button onClick={() => setShowAdhoc(true)} style={{
                  padding: '6px 14px', borderRadius: 8, border: '1px solid #86efac', background: '#dcfce7',
                  color: '#166534', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <FaUserPlus size={11} /> Add Ad-hoc Patient
                </button>
              ) : (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 6 }}>
                    Search for patients assigned to {therapist} (without scheduled appointments in leave period):
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="text" value={adhocSearch} onChange={e => searchAdhocPatients(e.target.value)}
                      placeholder="Search by name or card ID..."
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #86efac', fontSize: 13, background: '#fff' }} />
                    <button onClick={() => { setShowAdhoc(false); setAdhocSearch(''); setAdhocResults([]); }}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>
                      Done
                    </button>
                  </div>
                  {adhocResults.length > 0 && (
                    <div style={{ marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', maxHeight: 160, overflowY: 'auto' }}>
                      {adhocResults.map(function (pat) {
                        return (
                          <div key={pat.patientCard} onClick={() => addAdhocPatient(pat)}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                            <span><strong>{pat.patientName}</strong> <span style={{ color: '#94a3b8', fontSize: 11 }}>({pat.patientCard})</span></span>
                            <FaPlus size={10} style={{ color: '#16a34a' }} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Extend section */}
          {isActive && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a' }}>
              {!extending ? (
                <button onClick={() => setExtending(true)} style={{
                  padding: '6px 14px', borderRadius: 8, border: '1px solid #fbbf24', background: '#fef3c7',
                  color: '#92400e', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <FaCalendarPlus size={11} /> Extend Leave
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>New End Date:</label>
                  <input type="date" value={extendDate} onChange={e => setExtendDate(e.target.value)}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fbbf24', fontSize: 12 }} />
                  <button onClick={handleExtend} disabled={loading}
                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Extend
                  </button>
                  <button onClick={() => setExtending(false)}
                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e2e8f0',
          display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap',
          position: 'sticky', bottom: 0, background: '#fff', borderRadius: '0 0 16px 16px',
        }}>
          {isDraft && (
            <>
              <button onClick={handleSaveDraft} disabled={loading}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: '#f8fafc', color: '#334155', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <FaSave size={12} /> Save Draft
              </button>
              <button onClick={handleSubmit} disabled={loading}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #4338ca, #6366f1)', color: '#fff',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(67,56,202,0.25)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <FaCheck size={12} /> Submit Handover
              </button>
            </>
          )}
          {isActive && canRevert && (
            <button onClick={handleRevert} disabled={loading}
              style={{
                padding: '8px 18px', borderRadius: 8, border: '1px solid #fca5a5',
                background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <FaUndo size={12} /> Revert Handover
            </button>
          )}
          <button onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HandoverModal;
